import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { LayoutContext } from 'contexts';
import { Anchor, Text, Tooltip, FileViewer, theme } from 'components';
import { formatDocumentType } from 'utils';
import DocumentsContext from './DocumentsContext';

const DocumentsAside: React.FC = () => {
  const { scrollContainer } = React.useContext(LayoutContext);
  const { documentsToView } = React.useContext(DocumentsContext);

  const { pathname } = useLocation();
  const [lastActivePage, setLastActivePage] =
    React.useState<[number, number]>();

  const handleChange = (currentActiveLink: string) => {
    if (!currentActiveLink) return;
    const [fileId, pageNumber] = currentActiveLink
      .replace(`${pathname}/#`, '')
      .split('-');
    const fileIndex = documentsToView.findIndex(file => fileId === file.id);

    if (fileIndex === -1) throw new Error("Can't find index.");

    setLastActivePage([fileIndex, Number(pageNumber)]);
  };

  const checkIfActive = (fileId: string, pageNumber: number) => {
    const fileIndex = documentsToView.findIndex(file => fileId === file.id);

    if (!lastActivePage) return fileIndex === 0 && pageNumber === 0;
    if (fileIndex === -1) return false;

    const [activeIndex, activePageNumber] = lastActivePage;

    if (fileIndex !== activeIndex) {
      return fileIndex < activeIndex;
    }

    return pageNumber <= activePageNumber;
  };

  const zeroIndex = (pageNumber = 1) => pageNumber - 1;

  if (!scrollContainer || !documentsToView.length) return null;

  return (
    <StyledAnchor
      onChange={handleChange}
      onClick={e => e.preventDefault()}
      showInkInFixed={false}
      affix={false}
      targetOffset={25}
      getContainer={() => scrollContainer}
      style={{ maxHeight: 'initial' }}
    >
      <FileViewer
        hideSpin
        files={documentsToView}
        width={150}
        render={(Image, file, page) => (
          <Tooltip
            title={
              <StyledTooltipText textAlign="center">
                {formatDocumentType(file.documentType)}
              </StyledTooltipText>
            }
            getPopupContainer={el => el.parentElement}
          >
            <div>
              <StyledLink
                href={`${pathname}/#${file.id}-${zeroIndex(page?.pageNumber)}`}
                key={`${file.id}-${zeroIndex(page?.pageNumber)}`}
                title={
                  <StyledThumbnail
                    $active={checkIfActive(
                      file.id,
                      zeroIndex(page?.pageNumber),
                    )}
                  >
                    {Image}
                  </StyledThumbnail>
                }
              >
                <Text textAlign="center">{page?.pageNumber || 1}</Text>
              </StyledLink>
            </div>
          </Tooltip>
        )}
      />
    </StyledAnchor>
  );
};

const StyledAnchor = styled(Anchor)`
  background-color: transparent;
  height: auto;
  margin: 0;
  padding: 0;
  overflow: visible;

  .ant-anchor {
    padding-left: 0;
  }
  .ant-anchor-ink {
    display: none;
  }
`;

const StyledLink = styled(Anchor.Link)`
  padding: 0;
  > * {
    overflow: visible;
  }
`;

const StyledTooltipText = styled(Text)`
  color: white;
`;

const StyledThumbnail = styled.div<{ $active: boolean }>`
  border: 5px solid white;
  overflow: hidden;
  ${({ $active }) =>
    $active &&
    `
  border-color: ${theme.getColor('blue3')};
`}
  box-shadow: 0px 0px 4px 1px rgba(0, 0, 0, 0.15);
`;

export default DocumentsAside;
